import type { FieldDef } from '@/modules/catalog'

type Rules = NonNullable<FieldDef['numeric']>
type NumericField = Pick<FieldDef, 'key' | 'kind' | 'numeric'>
export type NumericIssue = 'number' | 'integer' | 'precision' | 'digits' | 'minimum' | 'maximum'

// Existing database contracts, not display rounding. Unspecified decimals
// receive syntax checks only; stock movements may legitimately be signed.
const decimalRules: Record<string, Rules> = {
  tax_rate: { precision: 4, maxDigits: 7, min: '0', max: '100' },
  duty_rate: { precision: 4, min: '0', max: '100' },
  conversion_rate: { precision: 6, maxDigits: 18, min: '0.000001' },
  gross_weight_kg: { precision: 3, maxDigits: 18, min: '0.001' },
  tare_weight_kg: { precision: 3, maxDigits: 18, min: '0' },
  volume_m3: { precision: 6, maxDigits: 18, min: '0.000001' },
  purchase_pack_multiple: { precision: 3, maxDigits: 18, min: '0.001' },
  capacity_m3: { precision: 3, maxDigits: 18, min: '0' },
  load_limit_kg: { precision: 3, maxDigits: 18, min: '0' },
  credit_limit: { precision: 4, maxDigits: 18, min: '0' },
  price: { precision: 4, maxDigits: 18, min: '0' },
  reference_price: { precision: 4, maxDigits: 18, min: '0' },
  reference_cost: { precision: 4, maxDigits: 18, min: '0' },
}
for (const key of ['storage_temp_min', 'storage_temp_max', 'transport_temp_min', 'transport_temp_max']) {
  decimalRules[key] = { precision: 1, maxDigits: 5 }
}
for (const key of ['capacity_warning_ratio', 'load_warning_ratio', 'expiry_warning_ratio', 'expiry_critical_ratio']) {
  decimalRules[key] = { precision: 4, maxDigits: 5, min: '0', max: '1' }
}

export function numericRules(field: NumericField): Rules {
  return { ...(field.kind === 'integer'
    ? { precision: 0, min: ['shelf_life_days', 'contained_quantity', 'units_per_pallet'].includes(field.key) ? '1' : '0',
      max: field.key === 'units_per_pallet' ? '999999999999999' : '2147483647' }
    : decimalRules[field.key]), ...field.numeric }
}

export function acceptsNumericDraft(value: string, kind: FieldDef['kind']): boolean {
  return (kind === 'integer' ? /^-?\d*$/ : /^-?\d*(\.\d*)?$/).test(value)
}

function compare(left: string, right: string): number {
  const places = Math.max(left.split('.')[1]?.length ?? 0, right.split('.')[1]?.length ?? 0)
  const scaled = (value: string) => {
    const negative = value.startsWith('-')
    const [whole = '', fraction = ''] = value.replace(/^-/, '').split('.')
    return BigInt((negative ? '-' : '') + (whole || '0') + fraction.padEnd(places, '0'))
  }
  const a = scaled(left), b = scaled(right)
  return a < b ? -1 : a > b ? 1 : 0
}

export function numericIssue(value: unknown, field: NumericField): NumericIssue | undefined {
  if (!['decimal', 'integer'].includes(field.kind ?? '') || value === '' || value == null) return
  const text = String(value)
  if (!/^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return 'number'
  if (field.kind === 'integer' && text.includes('.')) return 'integer'
  const rules = numericRules(field)
  const [whole = '', fraction = ''] = text.replace(/^-/, '').split('.')
  if (rules.precision !== undefined && fraction.length > rules.precision) return 'precision'
  const wholeDigits = whole.replace(/^0+/, '').length
  if (rules.maxDigits !== undefined && (wholeDigits + fraction.length > rules.maxDigits ||
    wholeDigits > rules.maxDigits - (rules.precision ?? 0))) return 'digits'
  if (rules.min !== undefined && compare(text, rules.min) < 0) return 'minimum'
  if (rules.max !== undefined && compare(text, rules.max) > 0) return 'maximum'
}

export function numericMessage(issue: NumericIssue, field: NumericField, locale: string): string {
  const rules = numericRules(field)
  const en = locale === 'en'
  switch (issue) {
    case 'number': return en ? 'Enter a number; letters and scientific notation are not accepted.' : '请输入数字，不接受文字或科学计数法。'
    case 'integer': return en ? 'Enter a whole number; fractions are not accepted.' : '请输入整数，不接受小数。'
    case 'precision': return en ? `Use at most ${rules.precision} decimal places.` : `最多允许 ${rules.precision} 位小数。`
    case 'digits': return en ? 'The number exceeds the supported precision.' : '数值超过该字段支持的位数。'
    case 'minimum': return en ? `Must be at least ${rules.min}.` : `数值不能小于 ${rules.min}。`
    case 'maximum': return en ? `Must be at most ${rules.max}.` : `数值不能大于 ${rules.max}。`
  }
}
